import {
  Group,
  Image,
  Paper,
  Stack,
  Text,
  Title,
  Pagination,
  MediaQuery,
  Skeleton,
  Center,
} from '@mantine/core';
import { useRouter } from 'next/router';
import { motion } from 'framer-motion';
import { InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import {AdvancedImage, lazyload, accessibility, responsive, placeholder} from '@cloudinary/react';
import { name as transformationName} from "@cloudinary/url-gen/actions/namedTransformation";
import TemplateResults, { CategoryData, TemplateData } from '../../components/TemplateResults';
import { TIER_IMAGES_CDN, TIER_IMAGES_TRANSFORM_CDN, WEBSITE_DOMAIN } from '../../src/Constants';
import { cld, supabaseAnonClient } from '../../src/Utils';

export default function CategoryPage({
  categoryData,
}: InferGetServerSidePropsType<typeof getStaticProps>) {
  const [categoryResults, setCategoryResults] = useState<CategoryData[]>([]);
  const [pageCount, setPageCount] = useState(1);
  const router = useRouter();
  const title = `${categoryData?.name} Tier List Templates | Lomo List | Best Tier List Maker`;
  const url = `${WEBSITE_DOMAIN}/categories/${encodeURIComponent(categoryData?.name)}`;
  const description = `${categoryData?.name} Tier List Maker. Create your own ${categoryData?.name} Tier List. Lomo is the best Tier List platform on the internet!
  See what's new in the ${categoryData?.name} Tier List Category!`;
  const image = `${
    categoryData?.thumbnail.includes('gif') ? TIER_IMAGES_CDN : TIER_IMAGES_TRANSFORM_CDN
  }/${categoryData?.thumbnail}?width=500&height=500`;



  const newImage = cld.image(categoryData?.thumbnail).namedTransformation(transformationName("web-category-thumbnail-1x"))
  const image1x = newImage.toURL()
  const image2x = cld.image(categoryData?.thumbnail).namedTransformation(transformationName("web-category-thumbnail-2x")).toURL()
  

  
  const pageQuery = router.query?.page
    ? Number.isNaN(Number(router.query.page))
      ? 1
      : Number(router.query.page)
    : 1;
  const [currentPage, setCurrentPage] = useState(pageQuery);
  const RESULTS_PER_PAGE = 24;
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      const { data, error, count } = await supabaseAnonClient
        .from('template_stats')
        .select('*', { count: 'exact' })
        .eq('category', categoryData?.name)
        .eq('visibility', 'public')
        .order('quality_score', { ascending: false })
        .range((currentPage - 1) * RESULTS_PER_PAGE, currentPage * RESULTS_PER_PAGE - 1);

      if (data) {
        setCategoryResults(data);
        setPageCount(count ? Math.ceil(count / RESULTS_PER_PAGE) : 0);
      }
      setIsLoading(false);
    }
    setCategoryResults([]);
    fetchData();
  }, [currentPage]);

  useEffect(() => {
    console.log(router.query);
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <Head>
        <meta name="title" content={title} />
        <title>{title}</title>
        <meta name="description" content={description} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${url}`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="og:site_name" content="LoMo" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${url}`} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content={image} />
        <link rel="canonical" href={url} />
      </Head>
      <Paper py="xl" px="xs">
        <MediaQuery smallerThan="sm" styles={{ display: 'none' }}>
          <Group mb="xl" maw={1200}>
            <AdvancedImage 
            srcset={`${image1x} 1x, ${image2x} 2x`}
            cldImg={newImage} width={200}
              height={200} alt={`${categoryData.name}. Create a ${categoryData.name} tier list. ${categoryData.name} Tier List Maker`}/>
            <Stack style={{ flex: 1 }}>
              <Title> {`${categoryData?.name} Tier Lists`}</Title>
              <Text size="md" color="dimmed" lineClamp={5}>
                {' '}
                {categoryData?.description}
              </Text>
            </Stack>
          </Group>
        </MediaQuery>
        <MediaQuery largerThan="sm" styles={{ display: 'none' }}>
          <Stack style={{ flex: 1, gap: 0 }}>
            <Title order={3}> {`${categoryData?.name} Tier List Templates`}</Title>
            <Text size="xs" my="xs" color="dimmed" lineClamp={4}>
              {' '}
              {categoryData?.description}
            </Text>
          </Stack>
        </MediaQuery>
        {/* {categoryResults.length > 0 ? (
          <> */}
        <TemplateResults
          templates={categoryResults ?? []}
          skeletonCount={RESULTS_PER_PAGE}
          isLoading={isLoading}
        />
        <Center>
          {pageCount > 1 && (
            <Pagination total={pageCount} onChange={setCurrentPage} value={currentPage} />
          )}
        </Center>
      </Paper>
    </motion.div>
  );
}

export async function getStaticPaths() {
  const { data } = await supabaseAnonClient.from('categories').select('name');
  if (!data || data.length === 0) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }
  const paths = data.map(({ name }) => ({ params: { slug: encodeURIComponent(name) } }));
  return {
    paths,
    fallback: 'blocking',
  };
}

export async function getStaticProps({ params }: { params: { slug: string } }) {
  try {
    const categoryName = decodeURIComponent(params.slug);
    console.log(categoryName);

    if (categoryName === "NSFW") {
        return {
            notFound: true,
          };
    }

    console.time('getCategoryData');
    const { data, error } = await supabaseAnonClient
      .from('categories')
      .select('*')
      .eq('name', categoryName)
      .single();
    console.timeEnd('getCategoryData');

    if (error) {
      console.error('Error loading category data', error);
      throw new Error(error.message);
    }
    return {
      props: {
        categoryData: data,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
}
